import { useEffect, useRef, useState } from "react";

/**
 * useReveal — fires a one-shot fade-in when the element enters the viewport.
 * Usage:
 *   const { ref, visible } = useReveal();
 *   <div ref={ref} className={visible ? "tbb-rise-in" : ""} style={visible ? {} : { opacity: 0 }} />
 *
 * The .tbb-rise-in keyframe lives in /app/frontend/src/index.css and already
 * respects prefers-reduced-motion.
 */
export function useReveal({ threshold = 0.05, rootMargin = "0px 0px -60px 0px" } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { rootMargin, threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin, threshold]);

  return { ref, visible };
}
